import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

interface EditSetNameModalProps {
  isOpen: boolean;
  currentName: string;
  onSave: (newName: string) => void;
  onCancel: () => void;
  title?: string;
  saveButtonText?: string;
}

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
`;

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  min-height: 100vh;
  padding: 40px 0 160px 0;
  box-sizing: border-box;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  box-shadow: var(--modal-shadow-large);
  margin: auto;
  border: var(--modal-border);
`;

const Title = styled.h2`
  margin: 0 0 20px 0;
  color: var(--color-text-primary);
  font-size: 1.3rem;
  text-align: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 1rem;
  margin-bottom: 20px;
  box-sizing: border-box;
  text-align: center;
  
  &:focus {
    outline: none;
    border-color: rgba(187, 187, 187, 1);
    box-shadow: 0 0 0 2px rgba(187, 187, 187, 0.6);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const Button = styled.button<{ primary?: boolean; disabled?: boolean }>`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  
  ${props => props.primary ? `
    background: ${props.disabled ? 'var(--color-panel)' : 'var(--color-accent-strong)'};
    color: ${props.disabled ? '#999' : 'white'};
    
    &:hover {
      background: ${props.disabled ? 'var(--color-panel)' : 'var(--color-accent)'};
    }
  ` : `
    background: #bbb;
    color: white;
    
    &:hover {
      background: #999;
    }
  `}
  
  &:focus {
    outline: none;
    box-shadow: ${props => props.primary ? '0 0 0 2px rgba(var(--color-accent-rgb), 0.3)' : '0 0 0 2px rgba(187, 187, 187, 0.6)'};
  }
`;

const EditSetNameModal: React.FC<EditSetNameModalProps> = ({
  isOpen,
  currentName,
  onSave,
  onCancel,
  title = "알람 세트 이름 수정",
  saveButtonText = "저장"
}) => {
  const [name, setName] = useState(currentName);

  // 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setName(currentName);
    }
  }, [isOpen, currentName]);

  const handleSave = () => {
    if (name.trim()) {
      onSave(name.trim());
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalOverlay isOpen={isOpen} />
      <ModalWrapper>
      <ModalContent>
        <Title>{title}</Title>
        <Input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="알람 세트 이름을 입력하세요"
          autoFocus
        />
        <ButtonGroup>
          <Button onClick={onCancel}>
            취소
          </Button>
          <Button primary onClick={handleSave} disabled={!name.trim()}>
            {saveButtonText}
          </Button>
        </ButtonGroup>
      </ModalContent>
      </ModalWrapper>
    </>
  );
};

export default EditSetNameModal; 